import { useTranslation } from 'react-i18next';
import { X, FolderInput, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';

interface SelectionBarProps {
  count: number;
  onClear: () => void;
  onMove: () => void;
  onDelete: () => void;
}

export function SelectionBar({ count, onClear, onMove, onDelete }: SelectionBarProps) {
  const { t } = useTranslation('projectFiles');

  return (
    <div
      className="flex items-center gap-2 border-b bg-muted/60 px-3 py-1.5"
      data-testid="project-files-selection-bar"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={onClear}
            aria-label={t('toolbar.clearSelection')}
            data-testid="project-files-selection-bar-clear"
          >
            <X className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('toolbar.clearSelection')}</TooltipContent>
      </Tooltip>

      <span className="text-sm font-medium text-foreground select-none">
        {t('toolbar.selected', { count })}
      </span>

      <div className="flex items-center gap-0.5 ml-auto">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={onMove}
              aria-label={t('toolbar.moveSelected')}
              data-testid="project-files-selection-bar-move"
            >
              <FolderInput className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('toolbar.moveSelected')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-destructive hover:text-destructive"
              onClick={onDelete}
              aria-label={t('toolbar.deleteSelected')}
              data-testid="project-files-selection-bar-delete"
            >
              <Trash2 className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('toolbar.deleteSelected')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
